import type {
  WorkspaceCreateErrorCode,
  WorkspaceDraftDocument,
  WorkspaceDraftStatus,
  WorkspaceStep,
  WorkspaceStepStatus,
} from '../workspace/workspace.types';

/** Wire payload for the ``open_workspace`` ui action (snake_case, from the BE). */
export type VoiceUiOpenWorkspaceData = {
  component: {
    kind: 'draft-document';
    document_kind: 'referral-letter' | 'email' | 'session-document' | 'note';
    title: string;
    subtitle?: string;
    session_id?: string;
    content?: string;
    status?: string;
    steps?: Array<{ id: string; label: string; status?: string }>;
  };
};

/** Wire payload for the ``workspace_update`` ui action (snake_case, from the BE). */
export type VoiceUiWorkspaceUpdateData = {
  content_delta?: string;
  content?: string;
  status?: string;
  steps?: Array<{ id: string; label: string; status?: string }>;
  step?: { id: string; status?: string };
  error?: string | null;
};

const DRAFT_STATUSES: readonly WorkspaceDraftStatus[] = [
  'streaming',
  'ready',
  'creating',
  'created',
];
const STEP_STATUSES: readonly WorkspaceStepStatus[] = ['pending', 'active', 'done'];
// The BE (ml-scribe `domains/voice/service/workspace_draft.py`) sends prose,
// not codes — its missing-session error is "No session to create the document
// in." — so detection is substring-based over the normalised string.
const NO_SESSION_ERROR_PATTERNS = [
  'missing_session',
  'no_session',
  'session_not_found',
  'no session',
  'session not found',
];

const toDraftStatus = (raw: string | undefined): WorkspaceDraftStatus =>
  DRAFT_STATUSES.includes(raw as WorkspaceDraftStatus)
    ? (raw as WorkspaceDraftStatus)
    : 'streaming';

const toStepStatus = (raw: string | undefined): WorkspaceStepStatus =>
  STEP_STATUSES.includes(raw as WorkspaceStepStatus)
    ? (raw as WorkspaceStepStatus)
    : 'pending';

const toCreateErrorCode = (raw: string): WorkspaceCreateErrorCode => {
  const normalized = raw.trim().toLowerCase();
  return NO_SESSION_ERROR_PATTERNS.some((pattern) => normalized.includes(pattern))
    ? 'no_session'
    : 'create_failed';
};

const toSteps = (raw: VoiceUiOpenWorkspaceData['component']['steps']): WorkspaceStep[] =>
  (raw ?? [])
    .filter((step) => typeof step?.id === 'string' && typeof step?.label === 'string')
    .map((step) => ({
      id: step.id,
      label: step.label,
      status: toStepStatus(step.status),
    }));

/**
 * Map an ``open_workspace`` wire payload to a workspace open init. Only the
 * draft-document kind arrives this way (the info board is driven by
 * ``present_info``), so the return type carries the concrete draft component.
 * Returns ``null`` on an invalid/unsupported payload (fail closed — the caller
 * logs and drops it rather than opening an empty overlay).
 */
export const mapOpenWorkspaceData = (
  data: VoiceUiOpenWorkspaceData
): { component: WorkspaceDraftDocument } | null => {
  const component = data?.component;
  if (!component || component.kind !== 'draft-document') return null;
  const title = component.title?.trim();
  if (!title) return null;

  return {
    component: {
      kind: 'draft-document',
      documentKind: component.document_kind,
      title,
      ...(component.subtitle?.trim() ? { subtitle: component.subtitle.trim() } : {}),
      ...(component.session_id?.trim() ? { sessionId: component.session_id.trim() } : {}),
      content: component.content ?? '',
      status: toDraftStatus(component.status),
      steps: toSteps(component.steps),
    },
  };
};

/**
 * Validated view of a ``workspace_update`` payload, normalised to store enums.
 * Returns ``null`` when nothing applicable is present (fail closed).
 */
export type WorkspaceUpdate = {
  contentDelta?: string;
  content?: string;
  status?: WorkspaceDraftStatus;
  steps?: WorkspaceStep[];
  step?: { id: string; status: WorkspaceStepStatus };
  /** `null` clears a previous commit error. */
  error?: WorkspaceCreateErrorCode | null;
};

export const mapWorkspaceUpdateData = (
  data: VoiceUiWorkspaceUpdateData
): WorkspaceUpdate | null => {
  if (!data || typeof data !== 'object') return null;
  const update: WorkspaceUpdate = {};
  if (typeof data.content_delta === 'string' && data.content_delta.length > 0) {
    update.contentDelta = data.content_delta;
  }
  if (typeof data.content === 'string') {
    update.content = data.content;
  }
  if (typeof data.status === 'string' && data.status) {
    update.status = toDraftStatus(data.status);
  }
  if (Array.isArray(data.steps)) {
    update.steps = toSteps(data.steps);
  }
  if (data.step && typeof data.step.id === 'string' && data.step.id) {
    update.step = { id: data.step.id, status: toStepStatus(data.step.status) };
  }
  if (data.error === null) {
    update.error = null;
  } else if (typeof data.error === 'string' && data.error.trim()) {
    update.error = toCreateErrorCode(data.error);
  }
  return Object.keys(update).length > 0 ? update : null;
};
